import { ActionIcon, TextInput } from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons';
import { useEffect, useRef, useState } from 'react';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  debounceMs?: number;
  placeholder?: string;
  width?: number | string;
}

export function SearchInput({
  value,
  onChange,
  debounceMs = 300,
  placeholder = 'Search…',
  width = 280,
}: SearchInputProps) {
  const [draft, setDraft] = useState(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    setDraft(value);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [value]);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const scheduleEmit = (next: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      onChangeRef.current(next);
    }, debounceMs);
  };

  const handleTyping = (next: string) => {
    setDraft(next);
    scheduleEmit(next);
  };

  const handleClear = () => {
    setDraft('');
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    onChangeRef.current('');
  };

  return (
    <TextInput
      value={draft}
      onChange={(event) => handleTyping(event.currentTarget.value)}
      icon={<IconSearch size={16} />}
      rightSection={
        draft ? (
          <ActionIcon
            size="sm"
            variant="subtle"
            color="gray"
            onClick={handleClear}
            aria-label="Clear search"
          >
            <IconX size={14} />
          </ActionIcon>
        ) : null
      }
      placeholder={placeholder}
      sx={{ width }}
    />
  );
}
